import { useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Returns true once the flag has been observed `true` at any point during the component's
 * lifetime, even if a later render returns `false`/`undefined` (e.g. while LD is mid-identify
 * and the manifest is empty). Use this for any flag read that gates in-flight UX across an
 * async boundary; live `useFlags()` reads are still correct for purely render-time gates.
 *
 * Kill-switch semantics still hold: a session that never observes `true` keeps the latch
 * `false` and the consumer falls through to its legacy path.
 */
export function useLatchedFlag(flagKey: string, scopeKey?: string | null, canLatch = true): boolean {
  const flags = useFlags();
  // Optional `scopeKey` (e.g. a dispensary id) ties the latch to it: a scope change clears it,
  // so every scope needs its own true. `canLatch` false (e.g. LD not yet identified for this
  // scope) only stops a new latch; an existing latch for the scope is kept.
  const latchRef = useRef<{ scope: string | null | undefined; isLatched: boolean }>({
    scope: scopeKey,
    isLatched: false,
  });
  if (latchRef.current.scope !== scopeKey) {
    latchRef.current = { scope: scopeKey, isLatched: false };
  }
  if (canLatch && flags[flagKey] === true) {
    latchRef.current.isLatched = true;
  }
  return latchRef.current.isLatched;
}
